import type { ComponentType } from 'react';
import type { AssistantDraft } from '../../types/assistant';

/** What a draft component receives: the draft as the coach has edited it so far. */
export interface AssistantDraftProps<TPayload = unknown> {
  draft: AssistantDraft<TPayload>;
  /** The coach changed the draft (a spot, a time, a message). The panel keeps the new payload. */
  onChange: (payload: TPayload) => void;
  /** The coach pressed Confirm. Calls the handler's `confirm`. */
  onConfirm: () => void;
  /** The coach pressed Discard. */
  onDiscard: () => void;
  /** True while `confirm` runs. */
  busy: boolean;
  /** The last confirm's error, for the coach. */
  error: string | null;
}

/** What a confirm needs from the app around it. */
export interface AssistantConfirmContext {
  navigate: (path: string) => void;
  /**
   * Puts a new payload on the draft while the confirm runs: a step that already happened (a board it created), so a
   * retry after a later step fails carries on from there instead of repeating it.
   */
  update?: (payload: unknown) => void;
}

/** What a confirm did, for the chat: "Saved 2027 Pitching with 6 players." and where to open it. */
export interface AssistantConfirmResult {
  message: string;
  openPath?: string | null;
  /** The link's words ("Open board"); "Open it" when left out. */
  openLabel?: string | null;
}

export interface AssistantDraftHandler<TPayload = unknown> {
  /**
   * Reads the draft's payload (unchecked JSON, or what a client `openDraft` passed) into `TPayload`, or null when it
   * is not readable; the panel then offers only Discard. A handler that types `TPayload` must give this. It must
   * accept its own output too, since an edited payload is read again.
   */
  read?: (raw: unknown) => TPayload | null;
  Component: ComponentType<AssistantDraftProps<TPayload>>;
  /**
   * Writes the draft through the existing endpoints (their validation, Page Access, plan features
   * and flags apply). Throws with words for the coach when it fails.
   */
  confirm: (draft: AssistantDraft<TPayload>, ctx: AssistantConfirmContext) => Promise<AssistantConfirmResult>;
  /**
   * Model-safe facts about the open draft, sent back each turn as `drafts[]` (counts, positions,
   * the program's own labels). Never names, contact details or notes. Leave it out to send nothing.
   */
  toContext?: (draft: AssistantDraft<TPayload>) => unknown;
}

const handlers = new Map<string, AssistantDraftHandler>();

/** Registers how drafts of `kind` render and confirm. Domains call this from `views/<domain>/`. */
export function registerDraftHandler<TPayload>(kind: string, handler: AssistantDraftHandler<TPayload>): void {
  handlers.set(kind, handler as AssistantDraftHandler);
}

/** The draft with its payload through the kind's `read`, or null when `read` refused it (or threw). */
export function readAssistantDraft(draft: AssistantDraft): AssistantDraft | null {
  const read = handlers.get(draft.kind)?.read;
  if (!read) return draft;
  try {
    const payload = read(draft.payload);
    return payload === null || payload === undefined ? null : { ...draft, payload };
  } catch {
    return null;
  }
}

/** The handler for `kind`, or undefined (the panel shows the summary with no Confirm). */
export function getDraftHandler(kind: string): AssistantDraftHandler | undefined {
  return handlers.get(kind);
}

/** Test only: forget every registration. */
export function resetDraftHandlersForTest(): void {
  handlers.clear();
}
